import { useState, useEffect } from "react";

import { useCurrentCampaign } from "hooks/api/use-campaigns";
import { useCurrentClient } from "hooks/api/use-clients";

import { IEmailType } from "containers/admin/clients/client/components/email-types/email-types.types";

import type {
  EmailAttributes,
  TouchpointVersionAttributes,
} from "hooks/api/use-touchpoint-versions";
import { getTouchpointVersion } from "generated/api/touchpoint-version";
import { mapTouchpointVersionDTO } from "utilities/api-mappers";
import { getEmailTypesRequest } from "hooks/api/use-email-types";

const touchpointVersionApi = getTouchpointVersion();

export const useEmailContentSettings = (
  touchpointId: string,
  selectedVersion?: TouchpointVersionAttributes,
) => {
  const currentClient = useCurrentClient();
  const currentCampaign = useCurrentCampaign();
  const [emailData, setEmailData] = useState<EmailAttributes | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    const fetchEmailData = async () => {
      if (!selectedVersion) {
        setEmailData(null);
        return;
      }
      let emailVersion = selectedVersion as EmailAttributes;
      if (!emailVersion.iterableEmailTypeId) {
        setIsLoading(true);
        try {
          const dto = await touchpointVersionApi.getOneVersion({
            clientId: currentClient.id,
            campaignId: currentCampaign.id,
            touchpointId,
            id: selectedVersion.id,
          });
          emailVersion = mapTouchpointVersionDTO(dto) as unknown as EmailAttributes;
        } catch (err) {
          console.error("Error fetching complete version data:", err);
          setEmailData(null);
          setIsLoading(false);
          return;
        }
      }

      setIsLoading(true);

      try {
        const emailTypes = (await getEmailTypesRequest(currentClient.id)) as unknown as IEmailType[];

        const emailType = emailTypes.find(
          (type: IEmailType) => type.id === emailVersion.iterableEmailTypeId,
        );

        if (emailType && emailType.senders.length > 0) {
          const sender =
            emailType.senders.find((s) => s.id === emailVersion.iterableEmailSenderId) ||
            emailType.senders.find((s) => s.defaultForEmailType) ||
            emailType.senders[0];

          const emailDataWithSender = {
            ...emailVersion,
            iterableEmailTypeName: emailType.externalName,
            senderName: sender.senderName,
            senderEmail: sender.senderEmail,
            replyToEmail: sender.replyToEmail,
          } as EmailAttributes;

          setEmailData(emailDataWithSender);
        } else {
          setEmailData(null);
        }
      } catch (err) {
        console.error("Error fetching email types:", err);
        setEmailData(null);
      } finally {
        setIsLoading(false);
      }
    };

    fetchEmailData();
  }, [selectedVersion, currentClient.id, currentCampaign.id, touchpointId]);

  return { emailData, isLoading };
};
